import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Title } from "../../../../themes/Default/components/Title";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Title as ListTitle } from "../../../../themes/Default/components/List/components/Header/components/Title";
import { studentsManagement } from "../../../../hooks/api/StudentsManagement";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { useTranslation } from "react-i18next";
import { Checkbox } from "../../../../themes/Default/components/Form/components/Checkbox";
import { Slide, toast, ToastContainer } from "react-toastify";
import Toaster from "../../../../themes/Default/components/Toaster/Toaster";

const Tests = () => {
    const { t } = useTranslation("common");
    const { studentId } = useParams();
    const navigate = useNavigate();
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedTests, setSelectedTests] = useState([]);
    const [tests, setTests] = useState();
    const [student, setStudent] = useState();
    const { useTestsListMutation, useDeleteTestMutation } = studentsManagement;
    const [testsList, { isLoading }] = useTestsListMutation();
    const [deleteTest] = useDeleteTestMutation();
    const { useViewStudentsMutation } = studentsManagement;
    const [viewStudent] = useViewStudentsMutation();

    const tabs = [
        { title: t("details"), onClick: () => gotoDetailsTab(studentId) },
        { title: t("students.tests"), isActive: true },
        { title: t("students.contract"), onClick: () => gotoContractsTab(studentId) },
        { title: t("calls.title"), onClick: () => gotoCallsTab(studentId) },
        { title: t("groups.title"), onClick: () => gotoGroupsTab(studentId) },
        { title: t("students.certificates"), onClick: () => gotoCertificatesTab(studentId) },
        { title: t("documents"), onClick: () => gotoDocumentsTab(studentId) },
        { title: t("notes"), onClick: () => gotoNotesTab(studentId) },
    ];

    const gotoCallsTab = (studentId) => {
        navigate("/students/" + studentId + "/calls");
    };

    const gotoContractsTab = (studentId) => {
        navigate("/students/" + studentId + "/contracts");
    };

    const gotoCertificatesTab = (studentId) => {
        navigate("/students/" + studentId + "/certificates");
    };

    const gotoDetailsTab = (studentId) => {
        navigate("/students/edit/" + studentId);
    };

    const gotoNotesTab = (studentId) => {
        navigate("/students/" + studentId + "/notes");
    };

    const gotoDocumentsTab = (studentId) => {
        navigate("/students/" + studentId + "/documents");
    };

    const gotoGroupsTab = (studentId) => {
        navigate("/students/" + studentId + "/groups");
    };

    const gotoNew = () => {
        navigate(`/students/${studentId}/tests/add`);
    };

    const gotoEdit = (testId) => {
        navigate(`/students/${studentId}/tests/${testId}/edit`);
    };

    const onSelectionChange = (id) => {
        setSelectedTests((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedTests?.length; i++) {
                            const testId = selectedTests[i];
                            deleteTest({ studentId, testId }).then((result) => {
                                testsList({ studentId }).then((result) => {
                                    const tests = result?.data?.records;
                                    setTests(tests);
                                });
                                setDisableDelete(true);
                                toast.info(result?.data?.info?.responseMessage);
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const formatDateToDMY = (value) => {

        const parts = value.split("-");

        const year = parts[0];
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);

        return `${day < 10 ? `0${day}` : day}.${month < 10 ? `0${month}` : month}.${year}`;
    };

    useEffect(() => {
        testsList({ studentId }).then((result) => {
            const tests = result?.data?.records;
            setTests(tests);
        });
    }, [studentId, testsList]);

    useEffect(() => {
        if (selectedTests.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedTests]);

    useEffect(() => {
        viewStudent(studentId).then((result) => {
            setStudent(result?.data);
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/students" />
                <Button title={t("students.addTests")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>
                    {student?.firstName} {student?.lastName}
                </Title>
                <Breadcrumps>
                    <Link title={t("students.title")} icon="fa-user-graduate" />
                    <Link title={t("students.tests")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader>
                            </ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("test.date")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("calendar.timeFrom")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("calendar.timeTo")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("students.typeName")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("groups.location")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("test.languageName")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("students.contractGroupType")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tests?.length > 0 &&
                                        tests?.map((test, index) => {
                                            return (
                                                <TableRow key={test.id}>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(test.id)}></Button>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={test.id} onChange={() => onSelectionChange(test.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <span style={{ color: test.isCanceled ? '#ff0000' : '' }}>{formatDateToDMY(test.date)}</span>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <span style={{ color: test.isCanceled ? '#ff0000' : '' }}>{test.timeFrom}</span>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <span style={{ color: test.isCanceled ? '#ff0000' : '' }}>{test.timeTo}</span>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <span style={{ color: test.isCanceled ? '#ff0000' : '' }}>{test.type}</span>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <span style={{ color: test.isCanceled ? '#ff0000' : '' }}>{test.locationName}</span>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <span style={{ color: test.isCanceled ? '#ff0000' : '' }}>{test.languageName}</span>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <span style={{ color: test.isCanceled ? '#ff0000' : '' }}>{test.courseTypeName}</span>
                                                    </TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Tests;
